/**
 * BTW Kwartier logo (UI spec §2), recreated as inline SVG from docs/ui-media/media/image2.png:
 * a clock-face ring — the top-right quarter (12→3) solid brand orange, the bottom-right quarter solid ink,
 * the left half cut into six equal 5-minute segments with thin parallel-edged gaps.
 * The orange quarter never moves or rotates. Ink is black in light mode, white in dark mode.
 */
import type { CSSProperties } from 'react';

const C = 50;
const R_OUT = 47;
const R_IN = 28;
const GAP = 3.2;

/** Point on a circle; angle in degrees clockwise from 12 o'clock. */
function pt(r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [C + r * Math.sin(a), C - r * Math.cos(a)];
}
const f = (n: number) => Math.round(n * 100) / 100;

/**
 * Annular sector from a0 to a1 whose cut edges are offset by half the gap, parallel to the radius,
 * so every gap has a constant width like the original artwork.
 */
export function sectorPath(a0: number, a1: number, gapStart = GAP, gapEnd = GAP): string {
  const h0 = gapStart / 2;
  const h1 = gapEnd / 2;
  const deg = (x: number) => (x * 180) / Math.PI;
  const o0 = a0 + deg(Math.asin(h0 / R_OUT));
  const o1 = a1 - deg(Math.asin(h1 / R_OUT));
  const i0 = a0 + deg(Math.asin(h0 / R_IN));
  const i1 = a1 - deg(Math.asin(h1 / R_IN));
  const large = o1 - o0 > 180 ? 1 : 0;
  const largeIn = i1 - i0 > 180 ? 1 : 0;
  const [ox0, oy0] = pt(R_OUT, o0);
  const [ox1, oy1] = pt(R_OUT, o1);
  const [ix1, iy1] = pt(R_IN, i1);
  const [ix0, iy0] = pt(R_IN, i0);
  return `M${f(ox0)} ${f(oy0)}A${R_OUT} ${R_OUT} 0 ${large} 1 ${f(ox1)} ${f(oy1)}L${f(ix1)} ${f(iy1)}A${R_IN} ${R_IN} 0 ${largeIn} 0 ${f(ix0)} ${f(iy0)}Z`;
}

const ORANGE = sectorPath(0, 90);
const INK_QUARTER = sectorPath(90, 180);
/** Six left segments, from 6 o'clock clockwise up to 12 o'clock (each = 5 minutes, 30°). */
const LEFT_SEGMENTS = Array.from({ length: 6 }, (_, i) => sectorPath(180 + i * 30, 210 + i * 30));
const SIMPLE_LEFT = sectorPath(180, 360, 0, 0);
const SIMPLE_ORANGE = sectorPath(0, 90, 0, 0);
const SIMPLE_INK = sectorPath(90, 180, 0, 0);

const INK = 'var(--brand-ink, #0A0A0A)';
const BRAND = 'var(--brand, #FC6000)';

interface MarkProps {
  size?: number;
  /** Simplified mark for < 24px: no segment gaps (§2 table). */
  simplified?: boolean;
  /** Always draw the full segmented mark, also below 24 px (e.g. the logo inside scaled-up website mock-ups). */
  detailed?: boolean;
  title?: string;
  className?: string;
  style?: CSSProperties;
}

export function LogoMark({ size = 32, simplified = false, detailed = false, title, className, style }: MarkProps) {
  const useSimple = !detailed && (simplified || size < 24);
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={style}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {useSimple ? (
        <>
          <path d={SIMPLE_LEFT} fill={INK} />
          <path d={SIMPLE_INK} fill={INK} />
          <path d={SIMPLE_ORANGE} fill={BRAND} />
        </>
      ) : (
        <>
          {LEFT_SEGMENTS.map((d, i) => (
            <path key={i} d={d} fill={INK} />
          ))}
          <path d={INK_QUARTER} fill={INK} />
          <path d={ORANGE} fill={BRAND} />
        </>
      )}
    </svg>
  );
}

/** Loading indicator: the six left segments light up in sequence; the orange quarter stays put (§9.7). */
export function LogoLoader({ size = 40, label }: { size?: number; label: string }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className="logo-loader" role="img" aria-label={label}>
      <g>
        {LEFT_SEGMENTS.map((d, i) => (
          <path key={i} d={d} fill={INK} className="seg" />
        ))}
      </g>
      <path d={INK_QUARTER} fill={INK} />
      <path d={ORANGE} fill={BRAND} />
    </svg>
  );
}

interface LogoProps {
  /** Show the Chinese name 一刻报税 under the wordmark (Chinese UI only, §2). */
  chinese?: boolean;
  height?: number;
  className?: string;
}

/**
 * Horizontal lockup: mark + wordmark ("BTW" bold serif caps, "Kwartier" regular serif, same baseline).
 * Width is derived from the height so the lockup never stretches.
 */
export function Logo({ chinese = false, height = 36, className }: LogoProps) {
  const vbH = chinese ? 112 : 100;
  const vbW = 446;
  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      height={height}
      width={(height * vbW) / vbH}
      className={className}
      role="img"
      aria-label={chinese ? 'BTW Kwartier 一刻报税' : 'BTW Kwartier'}
    >
      <g transform={chinese ? 'translate(0 6)' : undefined}>
        {LEFT_SEGMENTS.map((d, i) => (
          <path key={i} d={d} fill={INK} />
        ))}
        <path d={INK_QUARTER} fill={INK} />
        <path d={ORANGE} fill={BRAND} />
      </g>
      <text
        x="116"
        y={chinese ? 60 : 67}
        fill={INK}
        style={{ fontFamily: 'var(--font-logo)', fontSize: 50, letterSpacing: '-0.5px' }}
      >
        <tspan fontWeight={700}>BTW</tspan>
        <tspan fontWeight={400} dx="12">
          Kwartier
        </tspan>
      </text>
      {chinese && (
        <text x="118" y="100" fill={INK} style={{ fontFamily: 'var(--font-sans)', fontSize: 30, letterSpacing: '8px', fontWeight: 600 }}>
          一刻报税
        </text>
      )}
    </svg>
  );
}

/** Serialised simplified mark, used to write public/favicon.svg. */
export function faviconSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><style>.k{fill:#0A0A0A}@media (prefers-color-scheme:dark){.k{fill:#FFFFFF}}</style><path class="k" d="${SIMPLE_LEFT}"/><path class="k" d="${SIMPLE_INK}"/><path fill="#FC6000" d="${SIMPLE_ORANGE}"/></svg>`;
}
